import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Box } from "../../../components/ui";
import { campusApi } from "../campus-api";
import { Camera, CreateCameraDto, UpdateCameraDto } from "../types";
import { modalStyle } from "../../../style/common";

interface CameraFormModalProps {
  open: boolean;
  onClose: () => void;
  camera?: Camera | null;
  nodeId: string;
}

export default function CameraFormModal({ open, onClose, camera, nodeId }: CameraFormModalProps) {
  const [camId, setCamId] = useState("");
  const [rtspUrl, setRtspUrl] = useState("");
  const [type, setType] = useState<"entry" | "exit" | "both">("both");
  const [vehicleDetectionEnabled, setVehicleDetectionEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (camera) {
      setCamId(camera.cam_id || "");
      setRtspUrl(camera.rtspUrl);
      setType(camera.type);
      setVehicleDetectionEnabled(camera.vehicleDetectionEnabled);
    } else {
      setCamId("");
      setRtspUrl("");
      setType("both");
      setVehicleDetectionEnabled(false);
    }
  }, [camera, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rtspUrl.trim()) {
      toast.warning("Please enter an RTSP URL");
      return;
    }

    setLoading(true);
    try {
      if (camera) {
        const updateData: UpdateCameraDto = { cam_id: camId, rtspUrl, type, vehicleDetectionEnabled };
        await campusApi.updateCamera(camera.id, updateData);
        toast.success("Camera updated successfully");
      } else {
        const createData: CreateCameraDto = { nodeId, rtspUrl, type, vehicleDetectionEnabled };
        await campusApi.createCamera(createData);
        toast.success("Camera created successfully");
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save camera");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} open={open}>
      <Box sx={modalStyle}>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {camera ? "Edit Camera" : "Add Camera"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {camera && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Camera ID
                </label>
                <input
                  type="text"
                  value={camId}
                  onChange={(e) => setCamId(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter camera ID"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Unique identifier for this camera
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                RTSP URL <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={rtspUrl}
                onChange={(e) => setRtspUrl(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="rtsp://example.com/stream"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Camera Type <span className="text-red-500">*</span>
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as "entry" | "exit" | "both")}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="entry">Entry</option>
                <option value="exit">Exit</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Vehicle Detection/Logging
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enable automatic vehicle detection and logging
                </p>
              </div>
              <button
                type="button"
                onClick={() => setVehicleDetectionEnabled(!vehicleDetectionEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  vehicleDetectionEnabled
                    ? "bg-green-500"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    vehicleDetectionEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : camera ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
}

